import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useQuiz } from '../../contexts/QuizContext';
import QuizCreator from '../../components/quiz/QuizCreator';

const QuizEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentQuiz, quizLoading, quizError, fetchQuiz, clearCurrentQuiz } = useQuiz();
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    if (id) {
      fetchQuiz(parseInt(id));
    }
    
    // Cleanup when component unmounts
    return () => {
      clearCurrentQuiz();
    };
  }, [id, fetchQuiz, clearCurrentQuiz]);

  useEffect(() => {
    // Show editor when quiz is loaded
    if (currentQuiz && !quizLoading) {
      setShowEditor(true);
    }
  }, [currentQuiz, quizLoading]);

  const handleEditSuccess = (quizId: number) => {
    setShowEditor(false);
    navigate('/quizzes');
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    navigate('/quizzes');
  };

  if (quizLoading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (quizError) {
    return (
      <Container maxWidth="md">
        <Box py={4}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {quizError}
          </Alert>
          <Button
            variant="contained"
            startIcon={<BackIcon />}
            onClick={() => navigate('/quizzes')}
          >
            Quay lại danh sách bài tập
          </Button>
        </Box>
      </Container>
    );
  }

  if (!currentQuiz) {
    return (
      <Container maxWidth="md">
        <Box py={4}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Không tìm thấy bài tập
          </Alert>
          <Button
            variant="contained"
            startIcon={<BackIcon />}
            onClick={() => navigate('/quizzes')}
          >
            Quay lại danh sách bài tập
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={3}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate('/quizzes')}
            >
              Quay lại
            </Button>
            <Typography variant="h4" component="h1">
              Chỉnh sửa bài tập: {currentQuiz.title}
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            Cập nhật thông tin và cài đặt cho bài tập của bạn
          </Typography>
        </Paper>

        {/* Quiz Editor */}
        {showEditor && (
          <QuizCreator
            open={showEditor}
            onClose={handleCloseEditor}
            onSuccess={handleEditSuccess}
            editingQuiz={currentQuiz}
          />
        )}
      </Box>
    </Container>
  );
};

export default QuizEdit; 